import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Wrench, ListChecks, Anchor, GitMerge, Layers,
         PenTool, Cable, Map, ShieldAlert,
         RefreshCw, Package, Activity, Cloud,
         Sparkles, Eye, Lightbulb, BookOpen,
         PenLine, Boxes, Zap, Maximize2 } from "lucide-react";
import PipingSpecsPanel from "./engineering/PipingSpecsPanel";
import PipeSupportsPanel from "./engineering/PipeSupportsPanel";
import WeldsPanel from "./engineering/WeldsPanel";
import DrawingRegisterPanel from "./engineering/DrawingRegisterPanel";
import IsometricPanel from "./engineering/drawings/IsometricPanel";
import HookUpPanel from "./engineering/drawings/HookUpPanel";
import GAPanel from "./engineering/drawings/GAPanel";
import PlotPlanPanel from "./engineering/drawings/PlotPlanPanel";
import ConsistencyCheckerPanel from "./engineering/sync/ConsistencyCheckerPanel";
import MTOPanel from "./engineering/sync/MTOPanel";
import StressScreenerPanel from "./engineering/sync/StressScreenerPanel";
import PointCloudPanel from "./engineering/sync/PointCloudPanel";
import CommandBarPanel from "./engineering/ai/CommandBarPanel";
import DesignReviewerPanel from "./engineering/ai/DesignReviewerPanel";
import AutoCompletePanel from "./engineering/ai/AutoCompletePanel";
import TemplateLibraryPanel from "./engineering/ai/TemplateLibraryPanel";
import PIDEditor from "./engineering/editors/PIDEditor";
import ModelEditor3D from "./engineering/editors/ModelEditor3D";
import SLDEditor from "./engineering/editors/SLDEditor";
import { PANEL_STATUS, statusBadge, statusLabel } from "./engineering/_status";

const SECTIONS = [
  {
    title: "Library", desc: "Wave A — Engineering catalogues & registers",
    tabs: [
      { key: "specs", label: "Pipe Class Library", icon: ListChecks,
        desc: "Spec-driven pipe class catalogue · auto-select · material validator (ASME B31.3 / NACE)" },
      { key: "supports", label: "Pipe Support Manager", icon: Anchor,
        desc: "Support catalogue · auto-place per ASME B31.3 Table 321.1 · 3-D manifest" },
      { key: "welds", label: "Weld Management", icon: GitMerge,
        desc: "Auto-derive register · weld map · construction tracking dashboard" },
      { key: "drawings", label: "Drawing Register", icon: Layers,
        desc: "Drawing register · stage-gate workflow (DRAFT→IFR→IFA→APPROVED→IFC→AS-BUILT) · transmittals" },
    ],
  },
  {
    title: "Drawing Generators", desc: "Wave B — Auto-generate engineering deliverables",
    tabs: [
      { key: "iso", label: "Isometric", icon: PenTool,
        desc: "Auto-generate piping isometric SVG (30°/30° projection · welds · supports · BOM) — Ch 4" },
      { key: "hookup", label: "Instrument Hook-Up", icon: Cable,
        desc: "24 standard ISA hook-up templates (DP, radar, RTD, orifice, mag, control valve, PSV…) — Ch 12" },
      { key: "ga", label: "GA / Orthographic", icon: Map,
        desc: "Project the 3-D model to PLAN / ELEVATION / SECTION views with discipline filter — Ch 13" },
      { key: "plotplan", label: "Plot Plan + Hazardous Area", icon: ShieldAlert,
        desc: "Top-down plot plan with API 505 / IEC 60079-10-1 hazardous zone classification — Ch 14" },
    ],
  },
  {
    title: "Sync & Analysis", desc: "Wave D — P&ID↔3D consistency, MTO, stress + PCF export, point clouds",
    tabs: [
      { key: "sync", label: "P&ID ↔ 3D Sync", icon: RefreshCw,
        desc: "Bidirectional consistency checker — equipment, lines, instruments, endpoint connections — Ch 2" },
      { key: "mto", label: "MTO Extractor", icon: Package,
        desc: "Material take-off (piping by line + class · valves · steel · instruments) with XLSX/CSV export — Ch 7" },
      { key: "stress", label: "Pipe Stress + PCF", icon: Activity,
        desc: "8-rule stress screener + Intergraph PCF export → Caesar II / AutoPIPE / CAEPIPE / ROHR2 — Ch 10" },
      { key: "pointcloud", label: "Point Cloud", icon: Cloud,
        desc: "Upload existing-plant laser scans (E57/LAS/LAZ/PTS/PLY/XYZ) for brownfield design — Ch 17" },
    ],
  },
  {
    title: "AI Layer", desc: "Wave E — Capexara Process Industry AI powered design intelligence",
    tabs: [
      { key: "command", label: "Command Bar", icon: Sparkles,
        desc: "Natural-language design commands → structured edit operations on the PDO — Ch 19.1" },
      { key: "review", label: "Design Reviewer", icon: Eye,
        desc: "AI-driven review across safety, operability, constructability, standards & cost — Ch 19.2" },
      { key: "autocomplete", label: "Auto-Complete", icon: Lightbulb,
        desc: "Context-aware next-best-action suggestions when adding equipment / lines — Ch 19.3" },
      { key: "templates", label: "Template Library", icon: BookOpen,
        desc: "Pre-built reusable design assemblies (pump stations, PSV installs, flare systems, …) — Ch 19.4" },
    ],
  },
  {
    title: "Pro Editors", desc: "Wave C — Full-canvas P&ID, 3-D and SLD editors with AI assist",
    tabs: [
      { key: "pid-editor", label: "P&ID Editor", icon: PenLine,
        desc: "Drag-drop ISA-5.1 symbol palette · auto-tag · auto-line-id · 5-rule validation · revisions — Ch 5" },
      { key: "model-editor", label: "3-D Model Editor", icon: Boxes,
        desc: "Three.js viewport with parametric equipment · pipe routing · pipe-rack generator · clash detection — Ch 6" },
      { key: "sld-editor", label: "SLD Editor", icon: Zap,
        desc: "Electrical SLD canvas · 27 symbols · feeder routing · cable ampacity + voltage-drop validation — Ch 11" },
    ],
  },
];

const ALL_TABS = SECTIONS.flatMap((s) => s.tabs);

export default function EngineeringBackbone({ projectId }) {
  const [sub, setSub] = useState("specs");

  const wrapStyle = { display: "flex", flexDirection: "column", gap: 12, width: "100%" };
  const headerStyle = {
    padding: "12px 14px", background: "var(--pf-surface)",
    border: "1px solid var(--pf-border)", borderRadius: 8,
    display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
  };
  const sectionLabel = {
    fontSize: 9, fontWeight: 800, color: "var(--pf-muted)",
    textTransform: "uppercase", letterSpacing: 1, padding: "0 8px",
    alignSelf: "center", borderRight: "1px solid var(--pf-border)",
    marginRight: 4, paddingRight: 12,
  };
  const navStyle = {
    display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center",
    padding: 6, background: "var(--pf-surface)",
    border: "1px solid var(--pf-border)", borderRadius: 8,
  };
  const navBtn = (active) => ({
    padding: "6px 12px", fontSize: 12, fontWeight: 600,
    border: `1px solid ${active ? "var(--pf-ember)" : "transparent"}`,
    background: active ? "rgba(242,107,31,0.10)" : "transparent",
    color: "var(--pf-fg)", borderRadius: 6, cursor: "pointer",
    display: "flex", alignItems: "center", gap: 5,
  });

  const subDef = ALL_TABS.find((s) => s.key === sub) || ALL_TABS[0];
  const subSection = SECTIONS.find((sec) => sec.tabs.some((t) => t.key === sub)) || SECTIONS[0];
  // Map title → URL slug used by /pages/Engineering.jsx
  const sectionSlug = { "Library": "library", "Drawing Generators": "generators",
                          "Sync & Analysis": "sync", "AI Layer": "ai", "Pro Editors": "editors" }[subSection.title] || "library";

  return (
    <div data-testid="engineering-backbone" style={wrapStyle}>
      <div style={headerStyle}>
        <Wrench size={18} style={{ color: "#F26B1F", flexShrink: 0 }} />
        <div style={{ flex: "1 1 240px", minWidth: 0 }}>
          <div className="pf-h2" style={{ fontSize: 16, lineHeight: 1.2 }}>Engineering Backbone</div>
          <div className="pf-muted" style={{ fontSize: 11, marginTop: 2 }}>{subDef.desc}</div>
        </div>
        <Link to={`/projects/${projectId}/engineering/${sectionSlug}/${sub}`}
              data-testid="engg-fullscreen-btn"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 12px", fontSize: 11, fontWeight: 700,
                background: "#F26B1F", color: "#fff", borderRadius: 6,
                textDecoration: "none", whiteSpace: "nowrap",
              }}>
          <Maximize2 size={13} /> Open full-screen
        </Link>
      </div>

      {SECTIONS.map((section) => (
        <div key={section.title} style={navStyle} data-testid={`engg-section-${section.title.toLowerCase().replace(/\s+/g, "-")}`}>
          <div style={sectionLabel} title={section.desc}>{section.title}</div>
          {section.tabs.map((s) => {
            const Icon = s.icon;
            const meta = PANEL_STATUS[s.key];
            return (
              <button key={s.key} data-testid={`engg-${s.key}-tab`}
                      onClick={() => setSub(s.key)} style={navBtn(sub === s.key)}
                      title={meta ? `${statusLabel(meta.status)} — ${meta.note}` : undefined}>
                <Icon size={13} /> {s.label}
                {meta && <span data-testid={`engg-${s.key}-status-${meta.status.toLowerCase()}`}
                                style={statusBadge(meta.status)} />}
              </button>
            );
          })}
        </div>
      ))}

      <div>
        {sub === "specs"    && <PipingSpecsPanel projectId={projectId} />}
        {sub === "supports" && <PipeSupportsPanel projectId={projectId} />}
        {sub === "welds"    && <WeldsPanel projectId={projectId} />}
        {sub === "drawings" && <DrawingRegisterPanel projectId={projectId} />}
        {sub === "iso"      && <IsometricPanel projectId={projectId} />}
        {sub === "hookup"   && <HookUpPanel projectId={projectId} />}
        {sub === "ga"       && <GAPanel projectId={projectId} />}
        {sub === "plotplan" && <PlotPlanPanel projectId={projectId} />}
        {sub === "sync"       && <ConsistencyCheckerPanel projectId={projectId} />}
        {sub === "mto"        && <MTOPanel projectId={projectId} />}
        {sub === "stress"     && <StressScreenerPanel projectId={projectId} />}
        {sub === "pointcloud" && <PointCloudPanel projectId={projectId} />}
        {sub === "command"      && <CommandBarPanel projectId={projectId} />}
        {sub === "review"       && <DesignReviewerPanel projectId={projectId} />}
        {sub === "autocomplete" && <AutoCompletePanel projectId={projectId} />}
        {sub === "templates"    && <TemplateLibraryPanel projectId={projectId} />}
        {sub === "pid-editor"   && <PIDEditor projectId={projectId} />}
        {sub === "model-editor" && <ModelEditor3D projectId={projectId} />}
        {sub === "sld-editor"   && <SLDEditor projectId={projectId} />}
      </div>
    </div>
  );
}
